'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import {
  getNavigationDefinition,
  type NavigationContext,
} from '@/src/lib/navigation';
import { recordPageVisit } from '@/src/lib/navigation-recent';

export interface NavTrackerProps {
  activeContext: NavigationContext;
}

export function NavTracker({ activeContext }: NavTrackerProps) {
  const pathname = usePathname();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname || pathname === lastPath.current) return;
    lastPath.current = pathname;

    const normalized = pathname.length > 1 && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname;

    if (normalized === '/' || normalized === '/login') return;

    let bestMatch: { href: string; label: string; context: NavigationContext } | null = null;
    let bestLength = -1;

    const nav = getNavigationDefinition();
    for (const section of nav) {
      for (const group of section.groups) {
        for (const item of group.items) {
          if (!item.href) continue;
          const itemPath = item.href.split('?')[0] ?? item.href;
          const normalizedItem = itemPath.length > 1 && itemPath.endsWith('/')
            ? itemPath.slice(0, -1)
            : itemPath;
          if (normalized === normalizedItem || normalized.startsWith(`${normalizedItem}/`)) {
            if (normalizedItem.length > bestLength) {
              bestLength = normalizedItem.length;
              bestMatch = {
                href: item.href,
                label: item.label,
                context: section.context,
              };
            }
          }
        }
      }
    }

    if (bestMatch) {
      recordPageVisit(bestMatch.href, bestMatch.label, bestMatch.context);
    } else {
      const segments = normalized.split('/').filter(Boolean);
      const last = segments[segments.length - 1] ?? normalized;
      const label = last.replace(/-/g, ' ');
      recordPageVisit(normalized, label, activeContext);
    }
  }, [pathname, activeContext]);

  return null;
}